import { Icon, IconName, IconSize, MinimalIconProps } from '@ardoq/icons';
import { ReactNode, RefObject, useId, useRef } from 'react';
import {
  Popover2025,
  PopoverPlacement,
  Tooltip,
  useDelayedHover,
} from '@ardoq/popover2025';
import { FlexBox, Stack } from '@ardoq/layout';
import { Checkbox, Switch, TextInput } from '@ardoq/forms';
import { TertiaryButton } from '@ardoq/button';
import {
  DropdownContainer,
  DropdownFilterInput,
  DropdownMenu,
  DropdownOption,
  useDebouncedNestedPopover,
} from './Dropdown2026';
import { colors } from '@ardoq/design-tokens';
import { ColumnSelectMenu, ColumnSelectMenuProps } from './ColumnSelectMenu';

export type DropdownMenuProps = {
  content: MenuProps;
  isOpen: boolean;
  onClose: VoidFunction;
};

export const DropdownMenu2026 = (
  props: DropdownMenuProps & {
    anchorRef: RefObject<HTMLElement | null>;
    id: string;
    placement?: PopoverPlacement;
  }
) => {
  return (
    <>
      <Popover2025
        id={props.id}
        isOpen={props.isOpen}
        anchorRef={props.anchorRef}
        onClose={props.onClose}
        onClickOutside={props.onClose}
        placement={props.placement ?? { side: 'bottom', overflow: 'right' }}
      >
        <DropdownMenuContent {...props.content} />
      </Popover2025>
    </>
  );
};

export type MenuProps =
  | ActionsMenuProps
  | SelectMenuProps
  | FormMenuProps
  | ColumnSelectMenuProps;

const DropdownMenuContent = (
  props: MenuProps
): Exclude<ReactNode, undefined> => {
  switch (props.type) {
    case 'actions':
      return <ActionsMenu {...props} />;
    case 'form':
      return <FormMenu {...props} />;
    case 'select':
      return <SelectMenu {...props} />;
    case 'column-select':
      return <ColumnSelectMenu {...props} />;
  }
};

export type ActionsMenuProps = {
  type: 'actions';
  items: MenuItemProps[];
};

const hasAnyIcon = (items: MenuItemProps[]): boolean =>
  items.some(item => item.icon !== undefined);

const withDefaultIcon = (
  items: MenuItemProps[],
  shouldAlign: boolean
): MenuItemProps[] => {
  if (!shouldAlign) return items;
  return items.map(item =>
    item.icon === undefined ? { ...item, icon: { name: IconName.NONE } } : item
  );
};

const ActionsMenu = (props: ActionsMenuProps) => {
  const alignedItems = withDefaultIcon(props.items, hasAnyIcon(props.items));

  return (
    <DropdownContainer>
      <DropdownMenu>
        <Stack>
          {alignedItems.map((item, index) => (
            <MenuItem {...item} key={index} />
          ))}
        </Stack>
      </DropdownMenu>
    </DropdownContainer>
  );
};

export type SelectMenuProps = {
  type: 'select';
  items: SelectOptionProps[];

  filter?: {
    value: string;
    onValueChange: (value: string) => void;
    placeholder?: string;
  };
};

const SelectMenu = (props: SelectMenuProps) => {
  return (
    <DropdownContainer>
      <DropdownMenu>
        {props.filter && (
          <DropdownFilterInput
            placeholder={props.filter.placeholder ?? 'Filter...'}
            value={props.filter.value}
            onValueChange={props.filter.onValueChange}
          />
        )}
        <Stack>
          {props.items.map((item, index) => (
            <SelectOption {...item} key={index} />
          ))}
        </Stack>
      </DropdownMenu>
    </DropdownContainer>
  );
};

export type SelectOptionProps = {
  label: string;
  value: string;
  isSelected: boolean;
  onSelect: VoidFunction;
};

const SelectOption = (props: SelectOptionProps) => {
  const labelId = useId();
  const checkboxId = useId();
  return (
    <DropdownOption>
      <Checkbox
        inputId={checkboxId}
        aria-labelledby={labelId}
        name={props.label}
        isChecked={props.isSelected}
        onChange={props.onSelect}
      >
        <label id={labelId} htmlFor={checkboxId}>
          {props.label}
        </label>
      </Checkbox>
    </DropdownOption>
  );
};

export type FormMenuProps = {
  type: 'form';

  label: string;
  value: string;
  onValueChange: (value: string) => void;
  onCommit?: (value: string) => void;

  submit: {
    label: string;
    tooltip?: string;
    onSubmit: VoidFunction;
    isDisabled?: boolean;
  };
};

const FormMenu = (props: FormMenuProps) => (
  <DropdownContainer>
    <form
      onSubmit={e => {
        e.preventDefault();
        props.submit.onSubmit();
      }}
    >
      <FlexBox padding="small" gap="small" align="end">
        <TextInput
          label={props.label}
          value={props.value}
          onValueChange={props.onValueChange}
          onCommit={props.onCommit}
        />
        {/*TODO: support tooltip*/}
        <TertiaryButton type="submit" isDisabled={props.submit.isDisabled}>
          {props.submit.label}
        </TertiaryButton>
      </FlexBox>
    </form>
  </DropdownContainer>
);

export type MenuItemProps =
  | ButtonItemProps
  | InlineToggleItemProps
  | SubMenuItemProps;

const MenuItem = (props: MenuItemProps): Exclude<ReactNode, undefined> => {
  switch (props.type) {
    case 'button':
      return <ButtonItem {...props} />;
    case 'inline-toggle':
      return <InlineToggleItem {...props} />;
    case 'sub-menu':
      return <SubMenuItem {...props} />;
  }
};

export type ButtonItemProps = {
  type: 'button';

  label: string;
  icon?: MinimalIconProps;
  onClick: VoidFunction;

  isDisabled?: boolean;
  tooltip?: string;
};

const ButtonItemContent = (props: Pick<ButtonItemProps, 'label' | 'icon'>) => (
  <FlexBox align="center" gap="small">
    <Icon
      iconName={props.icon?.name}
      color={props.icon?.color || colors.icon}
      iconSize={IconSize.SMALL}
    />
    {props.label}
  </FlexBox>
);

const ButtonItem = (props: ButtonItemProps) => {
  if (props.tooltip === undefined) {
    return (
      <DropdownOption
        onClick={props.onClick}
        aria-label={props.label}
        isDisabled={props.isDisabled}
      >
        <ButtonItemContent icon={props.icon} label={props.label} />
      </DropdownOption>
    );
  }

  return <TooltipButtonItem {...props} tooltip={props.tooltip} />;
};

const TooltipButtonItem = (props: ButtonItemProps & { tooltip: string }) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const hover = useDelayedHover();
  const focus = useDelayedHover();
  const close = () => {
    hover.handleClose();
    focus.handleClose();
  };

  return (
    <>
      <DropdownOption
        ref={anchorRef}
        onClick={props.onClick}
        aria-label={props.label}
        isDisabled={props.isDisabled}
        onMouseEnter={hover.handleMouseEnter}
        onMouseLeave={hover.handleMouseLeave}
        onFocus={focus.handleMouseEnter}
        onBlur={focus.handleMouseLeave}
      >
        <ButtonItemContent icon={props.icon} label={props.label} />
      </DropdownOption>
      <Tooltip
        isOpen={hover.isOpen || focus.isOpen}
        onClose={close}
        anchorRef={anchorRef}
      >
        {props.tooltip}
      </Tooltip>
    </>
  );
};

export type InlineToggleItemProps = {
  type: 'inline-toggle';

  label: string;
  icon?: MinimalIconProps;
  value: boolean;
  onValueChange: (value: boolean) => void;

  isDisabled?: boolean;
  tooltip?: string;
};

const InlineToggleItem = (props: InlineToggleItemProps) => {
  return (
    <DropdownOption
      onClick={() => props.onValueChange(!props.value)}
      aria-label={props.label}
      isDisabled={props.isDisabled}
    >
      <FlexBox align="center" gap="small">
        <Icon
          iconName={props.icon?.name}
          color={props.icon?.color || colors.icon}
          iconSize={IconSize.SMALL}
        />
        {props.label}
        <FlexBox marginLeft="auto">
          {/*TODO: support tooltip*/}
          <Switch
            name={props.label}
            isChecked={props.value}
            onChange={props.onValueChange}
            aria-label={props.label}
            isDisabled={props.isDisabled}
            alignItemsCenter
          />
        </FlexBox>
      </FlexBox>
    </DropdownOption>
  );
};

export type SubMenuItemProps = {
  type: 'sub-menu';

  label: string;
  icon?: MinimalIconProps;
  content: MenuProps;

  isOpen: boolean;
  onOpen: VoidFunction;
  onClose: VoidFunction;

  /**
   * Builds the aria-label for the submenu popover. Receives the trigger
   * label so callers can localise the surrounding phrase.
   */
  subMenuAriaLabel?: (label: string) => string;
};

const defaultSubMenuAriaLabel = (label: string): string =>
  `Submenu for ${label}`;

const SubMenuItem = (props: SubMenuItemProps) => {
  const popoverId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const {
    handleTriggerMouseEnter,
    handleTriggerMouseLeave,
    handleChildMouseEnter,
    handleChildMouseLeave,
    childPopoverRef,
  } = useDebouncedNestedPopover(props.isOpen, isOpen =>
    isOpen ? props.onOpen() : props.onClose()
  );

  return (
    <>
      <DropdownOption
        onClick={props.onOpen}
        ref={triggerRef}
        aria-label={props.label}
        aria-haspopup="listbox"
        aria-expanded={props.isOpen}
        aria-controls={popoverId}
        onMouseEnter={handleTriggerMouseEnter}
        onMouseLeave={handleTriggerMouseLeave}
      >
        <FlexBox align="center" gap="small">
          <Icon
            iconName={props.icon?.name}
            color={props.icon?.color || colors.icon}
            iconSize={IconSize.SMALL}
          />
          {props.label}
          <FlexBox marginLeft="auto">
            <Icon
              color={colors.icon}
              iconSize={IconSize.MEDIUM}
              iconName={IconName.CHEVRON_RIGHT}
            />
          </FlexBox>
        </FlexBox>
      </DropdownOption>
      <Popover2025
        role="listbox"
        aria-label={(props.subMenuAriaLabel ?? defaultSubMenuAriaLabel)(
          props.label
        )}
        id={popoverId}
        isOpen={props.isOpen}
        anchorRef={triggerRef}
        onClose={props.onClose}
        onClickOutside={props.onClose}
        placement={{
          side: 'right',
          overflow: 'bottom',
        }}
      >
        <div
          ref={childPopoverRef}
          onMouseEnter={handleChildMouseEnter}
          onMouseLeave={handleChildMouseLeave}
        >
          <DropdownMenuContent {...props.content} />
        </div>
      </Popover2025>
    </>
  );
};
